import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Mail, MapPin, Linkedin } from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1F4E78] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[#1F4E78] font-bold text-xl">M</span>
              </div>
              <div>
                <span className="text-xl font-bold">Metasigna</span>
                <p className="text-xs text-gray-300">
                  {t('Next Generation Metabolic Oncology', 'Oncología Metabólica de Nueva Generación')}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-300 max-w-md">
              {t(
                'Transforming cancer immunotherapy through metabolic targeting. First-in-class dual-function platform addressing the €20B+ oncology market. Improving survival and adding years of life to oncology patients, 1.2-4.2 M life-years gained/year.',
                'Transformando la inmunoterapia del cáncer mediante direccionamiento metabólico. Plataforma de doble función de primera clase dirigida al mercado oncológico de €20B+. Mejorando la supervivencia y añadiendo años de vida a los pacientes oncológicos, 1,2-4,2 M años de vida ganados/año.'
              )}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold mb-4 text-[#FFD700]">
              {t('Quick Links', 'Enlaces Rápidos')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/investors" className="text-sm text-gray-300 hover:text-white transition-colors">
                  {t('For Investors', 'Para Inversores')}
                </Link>
              </li>
              <li>
                <Link to="/grants" className="text-sm text-gray-300 hover:text-white transition-colors">
                  {t('Public Funding', 'Financiación Pública')}
                </Link>
              </li>
              <li>
                <Link to="/partners" className="text-sm text-gray-300 hover:text-white transition-colors">
                  {t('Partnerships', 'Colaboraciones')}
                </Link>
              </li>
              <li>
                <Link to="/warcliv" className="text-sm text-gray-300 hover:text-white transition-colors">
                  {t('Warcliv (Educational)', 'Warcliv (Educacional)')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold mb-4 text-[#FFD700]">
              {t('Contact', 'Contacto')}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <Mail size={16} className="mt-1 text-[#FFD700]" />
                <a href="mailto:contact@metasigna.company" className="text-sm text-gray-300 hover:text-white transition-colors">
                  contact@metasigna.company
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <MapPin size={16} className="mt-1 text-[#FFD700]" />
                <span className="text-sm text-gray-300">
                  Harju maakond, Tallinn,<br />
                  Kesklinna linnaosa,<br />
                  Järvevana tee 9, 11314, Estonia
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <Linkedin size={16} className="mt-1 text-gray-500" />
                <span className="text-sm text-gray-500 cursor-not-allowed">
                  LinkedIn {t('(Coming soon)', '(Próximamente)')}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-400">
                © {currentYear} Metasigna. {t('All rights reserved.', 'Todos los derechos reservados.')}
              </p>
              <p className="text-sm text-gray-300 mt-1 font-medium">
                {t(
                  'A Netpioneer OÜ company',
                  'Una empresa de Netpioneer OÜ'
                )}
                <span className="text-gray-400 font-normal"> (Registry code: 16895690)</span>
              </p>
            </div>
            <p className="text-xs text-gray-400 text-center md:text-right max-w-2xl">
              {t(
                'Metasigna®, Clivamed® and Warcliv® are registered trademarks. Information is for informational purposes only and does not constitute medical advice. Technologies are in preclinical development.',
                'Metasigna®, Clivamed® y Warcliv® son marcas registradas. La información tiene carácter informativo y no constituye consejo médico. Las tecnologías están en fase de desarrollo preclínico.'
              )}
            </p>
          </div>
          {/* Web Design Credit */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              {t('Web design and development by', 'Diseño y realización web por')}{' '}
              <a 
                href="https://www.dimpsllc.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Dimps LLC
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
